import React from 'react';
import styles from './style.module.scss';
import { useRouter } from 'next/router';
import { formatCurrencyWithSpace } from '~/shared/utils';
import { useAppSelector } from '~/shared/hooks/redux';
import { useTranslation } from 'next-i18next';
import { useGetBestSellersQuery } from '~/features/pages/api';
import { BestSellerItem } from '~/features/pages/types';
import { Skeleton } from 'antd';

const BestSellers = () => {
    const { data: sellers, isLoading, isError } = useGetBestSellersQuery();
    const { user } = useAppSelector(state => state.auth)
    const { push } = useRouter();
    const { t } = useTranslation('common');

    if (isError) return null;

    const renderSkeletons = () => (
        <div className={styles.topSellerCards}>
            {[...Array(6)].map((_, i) => (
                <SellerCardSkeleton key={i} />
            ))}
        </div>
    );

    return (
        <section id="bests" className={styles.sellersSection}>
            <h1 className={styles.title}>{t('best_sellers.title')}</h1>

            {isLoading ? renderSkeletons() : (
                <div className={styles.topSellerCards}>
                    {sellers?.data?.map((seller, index: number) => (
                        <SellerCard
                            key={seller?.seller.id}
                            seller={seller}
                            index={index + 1}
                        />
                    ))}
                    {user?.access && sellers?.user_data.order && (
                        <>
                            <div className='d-none d-xl-block'>&nbsp;</div>
                            <SellerCard
                                seller={sellers?.user_data}
                                index={sellers?.user_data?.order}
                            />
                            <div className='d-none d-xl-block'>&nbsp;</div>
                        </>
                    )}
                </div>
            )}

            <div className={styles.btnCard}>
                {user?.access && sellers?.user_data ? (
                    <></>
                ) : (
                    <div className={styles.div}>
                        <h3>{t('best_sellers.join_cta')}</h3>
                        <button onClick={() => push('/auth/register')}>
                            {t('best_sellers.become_seller')}{' '}
                            <img
                                src="/static/img/HomePage/whitearrow.svg"
                                alt=""
                            />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default BestSellers;

interface SellerCardProps {
    seller: BestSellerItem & { order?: number }
    index: number
}

const SellerCard = ({ seller, index }: SellerCardProps) => {
    const { t } = useTranslation('common');

    return (
        <div
            onClick={() =>
                window.open(
                    `https://soff.uz/seller/${seller?.seller?.id}`,
                    '_blank'
                )
            }
            className={styles.sellerCard}>
            <div className={styles.imgBox}>
                {index <= 9 ? (
                    <img
                        src={`/static/img/HomePage/star${index}.svg`}
                        alt="star img"
                    />
                ) : (
                    <div>
                        <span
                            style={{
                                fontFamily: 'fantasy',
                                fontSize: '18px',
                                marginRight: '10px',
                            }}>
                            {seller.order}
                        </span>
                    </div>
                )}
            </div>
            <div className={styles.cardBody}>
                <img
                    className={styles.cardImg}
                    src={seller?.seller?.image || '/static/img/ozodbek.png'}
                    alt="seller_img"
                />
                <div className={styles.cardInfo}>
                    <h1>{seller?.seller?.full_name}</h1>
                    <div>
                        <img src="/static/svgs/product.svg" alt="product" />
                        <span>
                            {t('best_sellers.products', {
                                count: seller?.total_approved_documents,
                            })}{' '}
                        </span>
                    </div>
                    <div>
                        <img src="/static/svgs/coin.svg" alt="coin" />
                        <span>
                            {t('best_sellers.income', {
                                amount: formatCurrencyWithSpace(seller?.total_income),
                            })}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SellerCardSkeleton = () => {
    return (
        <div className={styles.sellerCard}>
            <div className={styles.imgBox} style={{ height: '30px' }}>
                <Skeleton.Avatar active size="small" shape="square" />
            </div>
            <div className={styles.cardBody}>
                <div className={styles.cardImg} style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
                    <Skeleton.Image active />
                </div>
                <div className={styles.cardInfo}>
                    <Skeleton.Button active size="small" style={{ height: '20px', width: '80%' }} />
                    <Skeleton.Input active size="small" style={{ height: '16px', width: '60%' }} />
                    <Skeleton.Input active size="small" style={{ height: '16px', width: '70%' }} />
                </div>
            </div>
        </div>
    );
};
